import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IoSettingsOutline,
  IoChevronForward,
  IoStarOutline,
  IoPersonOutline,
  IoPeopleOutline,
  IoTimeOutline,
  IoMegaphoneOutline,
  IoHelpCircleOutline,
  IoBusinessOutline,
  IoFlagOutline,
  IoLocationOutline,
  IoAddCircleOutline,
  IoWalletOutline,
  IoChatboxEllipsesOutline,
  IoDocumentTextOutline,
  IoInformationCircleOutline
} from 'react-icons/io5';
import FooterTabBarLayout from '../../layouts/FooterTabBarLayout';
import HeaderBar from '../../layouts/HeaderBar';
import useAuthStore from '../../../store/authStore';
import PointRiceIndicatorContainer from '../../containers/PointRiceIndicatorContainer';

const MyPage = () => {
  const navigate = useNavigate();
  const { member, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // 헤더 오른쪽 아이콘 설정
  const rightIcons = [
    {
      icon: <IoSettingsOutline size={24} className="text-gray-600" />,
      onClick: () => navigate('/app/settings'),
      title: '설정'
    }
  ];

  const renderCard = (title, items) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      {title && <h3 className="text-lg font-semibold mb-3">{title}</h3>}
      <div className="space-y-2">
        {items.map((item, index) => (
          <button
            key={index}
            onClick={() => item.onPress()}
            className="w-full flex items-center justify-between p-2 active:bg-gray-50 rounded-lg"
          >
            <div className="flex items-center">
              {item.icon}
              <span className="ml-3">{item.title}</span>
            </div>
            <IoChevronForward className="text-gray-400" />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <FooterTabBarLayout>
      <HeaderBar
        title="마이페이지"
        rightIcons={rightIcons}
      />

      <div className="p-4 max-w-lg mx-auto">
        {/* 프로필 카드 */}
        <div
          onClick={() => navigate('/app/settings/profile-edit')}
          className="bg-white rounded-lg p-4 mb-4 border border-gray-200 cursor-pointer active:bg-gray-50"
        >
          <div className="flex items-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden">
              {member?.imageUrl ? (
                <img
                  src={member.imageUrl}
                  alt="프로필"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-full h-full flex items-center justify-center text-gray-400"
                style={{ display: (member?.imageUrl) ? 'none' : 'flex' }}
              >
                <IoPersonOutline size={24} />
              </div>
            </div>
            <div className="flex-1 ml-4">
              <h2 className="text-lg font-semibold">{member?.name || '사용자'}</h2>
              <p className="text-sm text-gray-500">프로필 수정</p>
            </div>
            <IoChevronForward className="text-gray-400" />
          </div>

          {/* 밥 점수 인디케이터 */}
          <PointRiceIndicatorContainer point={member?.point} riceBalance={member?.riceBalance} />
        </div>

        {/* 빠른 액션 버튼 */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <button
            onClick={() => navigate('/app/mypage/location-selection')}
            className="flex items-center justify-center p-4 bg-white rounded-lg border border-gray-200"
          >
            <div className="relative">
              <IoLocationOutline className="text-gray-600 mr-2" />
            </div>
            <span className="ml-2">내 위치 설정</span>
          </button>
          <button
            onClick={() => navigate('/app/mypage/events')}
            className="flex items-center justify-center p-4 bg-white rounded-lg border border-gray-200"
          >
            <IoStarOutline className="text-gray-600 mr-2" />
            <span>이벤트</span>
          </button>
        </div>

        {/* 쌀 잔액 카드 */}
        {renderCard('쌀', [
          { title: '쌀 지갑', icon: <IoWalletOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/rice-wallet') },
          { title: '쌀 충전', icon: <IoAddCircleOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/rice-purchase') }
        ])}

        {/* 모임 관리 */}
        {renderCard('모임', [
          { title: '내가 모임장인 모임', icon: <IoFlagOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/hosted-meetings') },
          { title: '내가 참여한 모임', icon: <IoPeopleOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/joined-meetings') },
          { title: '승인 대기중인 모임', icon: <IoTimeOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/pending-meeting-applications') }
        ])}

        {/* 고객지원 */}
        {renderCard('고객지원', [
             member?.role === 'ROLE_PARTNER'
             ? { title: '내 제휴 매장 정보', icon: <IoBusinessOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/partner-place/my') }
             : { title: '제휴 매장 신청', icon: <IoBusinessOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/partner-place/form') },
          { title: '공지사항', icon: <IoMegaphoneOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/announcements') },
          { title: '자주 하는 질문', icon: <IoHelpCircleOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/frequent-questions') },
          { title: '고객센터', icon: <IoChatboxEllipsesOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/customer-center') },
          { title: '이용약관', icon: <IoDocumentTextOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/terms') },
          { title: '개인정보 처리방침', icon: <IoInformationCircleOutline className="text-gray-600" size={20} />, onPress: () => navigate('/app/mypage/privacy') },

        ].filter(Boolean))}

        {/* 회사 정보 */}
        <div className="text-xs text-gray-400 mt-8 mb-8 ml-4">
          <p className="font-bold mb-1">밥친구 정보</p>
          <p>주식회사 싱귤 (SINGULE Corp.)</p>
          <p>사업자등록번호: 558-87-03429</p>
        </div>
      </div>
    </FooterTabBarLayout>
  );
};

export default MyPage;
